/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { avy_api_pkg_communityinsights_stream_StreamJobRequest } from "../models/avy_api_pkg_communityinsights_stream_StreamJobRequest"
import type { batch_BatchJobRequest } from "../models/batch_BatchJobRequest"
import type { batch_CommunitiesAndTimePeriodRequest } from "../models/batch_CommunitiesAndTimePeriodRequest"
import type { batch_CommunitiesRequest } from "../models/batch_CommunitiesRequest"
import type { batch_CommunityScore } from "../models/batch_CommunityScore"
import type { batch_CountResponse } from "../models/batch_CountResponse"
import type { batch_GetFlagsResponse } from "../models/batch_GetFlagsResponse"
import type { batch_RateResponse } from "../models/batch_RateResponse"
import type { batch_ReadRateResponse } from "../models/batch_ReadRateResponse"
import type { batch_TimestampResponse } from "../models/batch_TimestampResponse"
import type { communityinsights_GetTopicsResponse } from "../models/communityinsights_GetTopicsResponse"

import type { CancelablePromise } from "../core/CancelablePromise"
import { OpenAPI } from "../core/OpenAPI"
import { request as __request } from "../core/request"

export class CommunityInsightsAdminService {
    /**
     * Get monthly scores by community for a given topic
     * Returns the monthly scores for the given communities and time period
     * @returns batch_CommunityScore OK
     * @throws ApiError
     */
    public static postV1AdminCommunityInsightsInsightsMonthlyCommunityScores({
        request,
    }: {
        /** communities: [optional], access_group_id: [optional], start_date: [required], end_date: [optional, default: time.now()]. Timeformat: YYYYMM **/
        request: batch_CommunitiesAndTimePeriodRequest
    }): CancelablePromise<Array<batch_CommunityScore>> {
        return __request(OpenAPI, {
            method: "POST",
            url: "/v1/admin/community-insights/insights/monthly-community-scores",
            body: request,
        })
    }

    /**
     * Get community topics for property owner
     * Returns the community topics that the property owner has access to
     * @returns communityinsights_GetTopicsResponse OK
     * @throws ApiError
     */
    public static getV1AdminCommunityInsightsInsightsTopic(): CancelablePromise<communityinsights_GetTopicsResponse> {
        return __request(OpenAPI, {
            method: "GET",
            url: "/v1/admin/community-insights/insights/topic",
        })
    }

    /**
     * Get engagement rate for the given communities and time period
     * Returns an engagement rate (= nbr engaged tenants/total nbr of tenants) for the given communities and time period
     * @returns batch_RateResponse OK
     * @throws ApiError
     */
    public static postV1AdminCommunityInsightsOverviewEngagementRate({
        request,
    }: {
        /** communities: [optional], access_group_id: [optional], start_date: [required], end_date: [optional, default: time.now()]. Timeformat: YYYYMM **/
        request: batch_CommunitiesAndTimePeriodRequest
    }): CancelablePromise<batch_RateResponse> {
        return __request(OpenAPI, {
            method: "POST",
            url: "/v1/admin/community-insights/overview/engagement-rate",
            body: request,
        })
    }

    /**
     * Get latest admin activity in communities
     * Returns the latest admin activity for the given communities
     * @returns batch_TimestampResponse OK
     * @throws ApiError
     */
    public static postV1AdminCommunityInsightsOverviewLatestAdminActivity({
        request,
    }: {
        /** communities: [optional] **/
        request: batch_CommunitiesRequest
    }): CancelablePromise<batch_TimestampResponse> {
        return __request(OpenAPI, {
            method: "POST",
            url: "/v1/admin/community-insights/overview/latest-admin-activity",
            body: request,
        })
    }

    /**
     * Get latest tenant activity in communities
     * Returns the latest tenant activity for the given communities
     * @returns batch_TimestampResponse OK
     * @throws ApiError
     */
    public static postV1AdminCommunityInsightsOverviewLatestTenantActivity({
        request,
    }: {
        /** communities: [optional] **/
        request: batch_CommunitiesRequest
    }): CancelablePromise<batch_TimestampResponse> {
        return __request(OpenAPI, {
            method: "POST",
            url: "/v1/admin/community-insights/overview/latest-tenant-activity",
            body: request,
        })
    }

    /**
     * Get created comments for communities and time period
     * Returns the total number of comments created for the given communities and time period.
     * @returns batch_CountResponse OK
     * @throws ApiError
     */
    public static postV1AdminCommunityInsightsOverviewNbrComments({
        request,
    }: {
        /** communities: [optional], access_group_id: [optional], start_date: [required], end_date: [optional, default: time.now()]. Timeformat: YYYYMM **/
        request: batch_CommunitiesAndTimePeriodRequest
    }): CancelablePromise<batch_CountResponse> {
        return __request(OpenAPI, {
            method: "POST",
            url: "/v1/admin/community-insights/overview/nbr-comments",
            body: request,
        })
    }

    /**
     * Get the flag distribution for the given communities and time period
     * Returns a list where each list represents a flag distribution for a community
     * @returns batch_GetFlagsResponse OK
     * @throws ApiError
     */
    public static postV1AdminCommunityInsightsOverviewNbrFlags({
        request,
    }: {
        /** communities: [optional], access_group_id: [optional], start_date: [required], end_date: [optional, default: time.now()]. Timeformat: YYYYMM **/
        request: batch_CommunitiesAndTimePeriodRequest
    }): CancelablePromise<batch_GetFlagsResponse> {
        return __request(OpenAPI, {
            method: "POST",
            url: "/v1/admin/community-insights/overview/nbr-flags",
            body: request,
        })
    }

    /**
     * Get created posts for communities and time period
     * Returns the total number of posts created for the given communities and time period.
     * @returns batch_CountResponse OK
     * @throws ApiError
     */
    public static postV1AdminCommunityInsightsOverviewNbrPosts({
        request,
    }: {
        /** communities: [optional], access_group_id: [optional], start_date: [required], end_date: [optional, default: time.now()]. Timeformat: YYYYMM **/
        request: batch_CommunitiesAndTimePeriodRequest
    }): CancelablePromise<batch_CountResponse> {
        return __request(OpenAPI, {
            method: "POST",
            url: "/v1/admin/community-insights/overview/nbr-posts",
            body: request,
        })
    }

    /**
     * Get read rate for the given communities and time period
     * Returns the read rate (= Number of unique reads of a post / total number of users in the community) for the given communities and time period
     * @returns batch_ReadRateResponse OK
     * @throws ApiError
     */
    public static postV1AdminCommunityInsightsOverviewReadRate({
        request,
    }: {
        /** communities: [optional], access_group_id: [optional], start_date: [required], end_date: [optional, default: time.now()]. Timeformat: YYYYMM **/
        request: batch_CommunitiesAndTimePeriodRequest
    }): CancelablePromise<batch_ReadRateResponse> {
        return __request(OpenAPI, {
            method: "POST",
            url: "/v1/admin/community-insights/overview/read-rate",
            body: request,
        })
    }

    /**
     * Manually re-publish community events to the event stream
     * Re-publishes community events for the given property owner and time period, which will trigger a re-classification of the LLM. The start & end times will be truncated to the start/end of each month.
     * @returns any OK
     * @throws ApiError
     */
    public static postV1AdminCommunityInsightsPublishCommunityEvents({
        request,
    }: {
        /** property_owner_ids: [required], start_month: [required], end_month: [optional]. Timeformat: YYYYMM **/
        request: avy_api_pkg_communityinsights_stream_StreamJobRequest
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: "POST",
            url: "/v1/admin/community-insights/publish-community-events",
            body: request,
        })
    }

    /**
     * Manual invocation of the community insights batch job
     * Runs the community insights batch job for the given property owner and time period. The start & end times will be truncated to the start/end of each month.
     * @returns any OK
     * @throws ApiError
     */
    public static postV1AdminCommunityInsightsRunBatch({
        request,
    }: {
        /** property_owner_ids: [required], start_month: [required], end_month: [required]. Timeformat: YYYYMM **/
        request: batch_BatchJobRequest
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: "POST",
            url: "/v1/admin/community-insights/run-batch",
            body: request,
        })
    }
}
